<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs()">
    <template #actions>
      <paywall-button
        :unlocked="isFeedbackFeatureAllowed"
        class="mr-2"
        color="success"
        :disabled="(!isChanged || !isLoaded) && isFeedbackFeatureAllowed"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>{{ $t('button.save_changes') }}
      </paywall-button>
    </template>
    <template #content>
      <v-alert
        v-if="s.noRuleError"
        class="mb-6"
        type="error"
        dense
        prominent
      >
        <v-row align="center">
          <v-col class="grow">
            {{ $t('error.feedback_no_rule_error') }}
          </v-col>
          <v-col class="shrink">
            <v-btn @click="configureMessage">
              {{ $t('button.configure_now') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-alert>
      <validation-observer ref="observerRef">
        <settings-group
          :title="$t('label.feedback')"
          :subtitle="$t('settings.feedback.feedback_subtitle')"
          gutter
        >
          <v-col
            cols="12"
            class="pt-2 pb-3"
          >
            <v-switch
              :input-value="s.enabled"
              class="pa-0 ma-0 pb-2"
              color="primary"
              :label="$i18n.t('title.enable_feedback')"
              hide-details
              :disabled="!isLoaded"
              @change="changeEnable"
            />
            {{ $t('label.help_enable_feedback') }}
          </v-col>
          <row-form-divider />

          <v-col
            cols="12"
            class="pt-2 pb-3"
          >
            <v-switch
              :input-value="s.useBreakdown && s.enabled"
              class="pa-0 ma-0 pb-2"
              color="primary"
              :label="$i18n.t('title.gather_detailed_feedback')"
              hide-details
              :disabled="!isLoaded || !s.enabled"
              @change="changeUseBreakdown"
            />
            {{ $t('label.help_allow_detailed_feedback') }}
          </v-col>
        </settings-group>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
import {
  Ref, computed, getCurrentInstance, onBeforeUnmount, onMounted, ref,
} from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import { isModelEqualToModel, cloneModel } from '@/model/model-utils';
import { performDispatchAction, performSaveAction } from '@/services/vue-utils';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import { ValidationObserver } from 'vee-validate';
import store from '@/store/store';
import FeedbackSettings from '@/grpc-api/model/feedback-settings';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import FeedbackStore from '@/store/feedback-store';
import PaywallButton from '@/components/buttons/PaywallButton.vue';
import { useRouter } from 'vue-router/composables';

const isFeedbackFeatureAllowed = computed<boolean>(() => store.getters.isFeedbackFeatureAllowed);

const router = useRouter();

const observerRef = ref(null as null | InstanceType<typeof ValidationObserver>);

const s = ref(new FeedbackSettings()) as Ref<FeedbackSettings>;
const olds = ref(cloneModel(s.value)) as Ref<FeedbackSettings>;
const isLoaded = ref(false) as Ref<boolean>;

const isChanged = computed(() => s.value && olds.value && !isModelEqualToModel(s.value, olds.value));

const changeEnable = (enabled: boolean) => {
  s.value.enabled = enabled;
  s.value.useBreakdown = (enabled && olds.value.useBreakdown === undefined) ? true : olds.value.useBreakdown;
};

const changeUseBreakdown = (useBreakdown: boolean) => { s.value.useBreakdown = useBreakdown; };

const load = async () => {
  const settings = await performDispatchAction('loadFeedbackSettings', {}) as FeedbackSettings | null;
  if (!settings) return;

  isLoaded.value = true;
  s.value = settings;
  olds.value = cloneModel(s.value);
};

const save = async () => {
  let newSettings: FeedbackSettings | undefined;
  const ok = await performSaveAction(
    observerRef.value,
    async () => { newSettings = await store.dispatch('setFeedbackSettings', { settings: s.value }); },
  );
  if (!ok || !newSettings) return;

  s.value = newSettings;
  olds.value = cloneModel(s.value);
};

const reset = async () => {
  store.dispatch('resetFeedback');
};

onBeforeUnmount(() => {
  unloadStoreModule(getCurrentInstance()?.proxy!, 'feedback', async () => { reset(); });
});

onMounted(() => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'feedback', FeedbackStore, async () => { load(); });
});

const configureMessage = () => router.replace('/settings/message-center?message-channel=email&event-type=tbe_thankyou');

</script>
